// pages/PlaylistsPage.jsx
// Page de gestion des playlists de vocabulaire

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Globe, Lock, Users, BookOpen, Upload } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './PlaylistsPage.css';

export default function PlaylistsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importResults, setImportResults] = useState(null);

  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    langueSource: 'fr',
    typeMot: '',
    isPublic: false,
    theme: '',
  });

  const userId = user.id;

  const langues = [
    { code: 'fr', label: t('langues.fr'), flag: '🇫🇷' },
    { code: 'es', label: t('langues.es'), flag: '🇪🇸' },
    { code: 'en', label: t('langues.en'), flag: '🇬🇧' },
  ];

  const themes = [
    { value: '', label: t('playlists.aucunTheme') },
    { value: 'voyage', label: t('playlists.themeVoyage') },
    { value: 'cuisine', label: t('playlists.themeCuisine') },
    { value: 'business', label: t('playlists.themeBusiness') },
    { value: 'quotidien', label: t('playlists.themeQuotidien') },
    { value: 'culture', label: t('playlists.themeCulture') },
    { value: 'sport', label: t('playlists.themeSport') },
    { value: 'technologie', label: t('playlists.themeTechnologie') },
    { value: 'nature', label: t('playlists.themeNature') },
    { value: 'famille', label: t('playlists.themeFamille') },
    { value: 'autre', label: t('playlists.themeAutre') },
  ];

  const typesMots = [
    { value: '', label: t('playlists.typeMotChoisir') },
    { value: 'nom', label: t('playlists.typeNom') },
    { value: 'verbe', label: t('playlists.typeVerbe') },
    { value: 'adjectif', label: t('playlists.typeAdjectif') },
    { value: 'adverbe', label: t('playlists.typeAdverbe') },
    { value: 'pronom', label: t('playlists.typePronom') },
    { value: 'expression', label: t('playlists.typeExpression') },
  ];

  // Charger les playlists
  const fetchPlaylists = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/playlists?userId=${userId}`);
      const data = await response.json();
      if (data.success) {
        setPlaylists(data.playlists);
      } else {
        throw new Error(t('playlists.erreurGenerique'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlaylists();
  }, []);

  // Créer ou modifier une playlist
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.typeMot) {
      alert(t('playlists.veuillezChoisirType'));
      return;
    }

    try {
      const url = editingPlaylist
        ? `/api/playlists/${editingPlaylist.id}`
        : '/api/playlists';
      const method = editingPlaylist ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          userId,
        }),
      });

      if (response.status === 429) {
        alert(t('playlists.limiteAtteinte'));
        return;
      }

      const data = await response.json();

      if (data.success) {
        alert(editingPlaylist ? t('playlists.playlistModifiee') : t('playlists.playlistCreee'));
        setShowCreateModal(false);
        setEditingPlaylist(null);
        resetForm();
        fetchPlaylists();
      } else {
        throw new Error(t('playlists.erreurGenerique'));
      }
    } catch (err) {
      alert(t('playlists.erreurPrefix') + err.message);
    }
  };

  // Supprimer une playlist
  const handleDelete = async (playlist) => {
    if (!window.confirm(t('playlists.confirmSupprimer', { nom: playlist.nom }))) return;

    try {
      const response = await fetch(`/api/playlists/${playlist.id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        fetchPlaylists();
      } else {
        throw new Error(t('playlists.erreurGenerique'));
      }
    } catch (err) {
      alert(t('playlists.erreurPrefix') + err.message);
    }
  };

  // Ouvrir le modal d'édition
  const handleEdit = (playlist) => {
    setEditingPlaylist(playlist);
    setFormData({
      nom: playlist.nom,
      description: playlist.description || '',
      langueSource: playlist.langueSource,
      typeMot: playlist.typeMot || '',
      isPublic: playlist.isPublic,
      theme: playlist.theme || '',
    });
    setShowCreateModal(true);
  };

  // Reset le formulaire
  const resetForm = () => {
    setFormData({
      nom: '',
      description: '',
      langueSource: 'fr',
      typeMot: '',
      isPublic: false,
      theme: '',
    });
  };

  // Ouvrir le modal de détails
  const handleOpenDetails = async (playlist) => {
    try {
      const response = await fetch(`/api/playlists/${playlist.id}`);
      const data = await response.json();
      if (data.success) {
        setSelectedPlaylist(data.playlist);
      }
    } catch (err) {
      alert(t('playlists.erreurPrefix') + err.message);
    }
  };

  // Importer des mots via copier-coller
  const handleImport = async () => {
    if (!importText.trim()) {
      alert(t('playlists.veuillezCollerTexte'));
      return;
    }

    try {
      const response = await fetch(`/api/playlists/${selectedPlaylist.id}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texte: importText }),
      });

      const data = await response.json();

      if (data.success) {
        setImportResults(data);
        // Recharger la playlist
        handleOpenDetails(selectedPlaylist);
      } else {
        throw new Error(t('playlists.erreurGenerique'));
      }
    } catch (err) {
      alert(t('playlists.erreurPrefix') + err.message);
    }
  };

  // Retirer un mot de la playlist
  const handleRemoveMot = async (motId) => {
    try {
      const response = await fetch(`/api/playlists/${selectedPlaylist.id}/mots/${motId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        handleOpenDetails(selectedPlaylist);
      } else {
        throw new Error(t('playlists.erreurGenerique'));
      }
    } catch (err) {
      alert(t('playlists.erreurPrefix') + err.message);
    }
  };

  const getLangueFlag = (code) => {
    const langue = langues.find(l => l.code === code);
    return langue ? langue.flag : code;
  };

  if (loading) {
    return (
      <div className="playlists-page">
        <h1>{t('playlists.title')}</h1>
        <div className="loading">{t('playlists.loading')}</div>
      </div>
    );
  }

  return (
    <div className="playlists-page">
      <div className="page-header">
        <h1>{t('playlists.title')}</h1>
        <button
          className="btn-create"
          onClick={() => {
            resetForm();
            setEditingPlaylist(null);
            setShowCreateModal(true);
          }}
        >
          <Plus size={20} />
          {t('playlists.nouvellePlaylist')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Liste des playlists */}
      <div className="playlists-grid">
        {playlists.length === 0 ? (
          <div className="empty-state">
            <BookOpen size={48} />
            <p>{t('playlists.aucunePlaylist')}</p>
            <p className="hint">{t('playlists.creezPremiere')}</p>
          </div>
        ) : (
          playlists.map((playlist) => (
            <div key={playlist.id} className="playlist-card">
              <div className="playlist-header">
                <h3>{playlist.nom}</h3>
                <div className="playlist-badges">
                  {playlist.isPublic ? (
                    <span className="badge badge-public" title={t('playlists.publique')}>
                      <Globe size={14} />
                    </span>
                  ) : (
                    <span className="badge badge-private" title={t('playlists.privee')}>
                      <Lock size={14} />
                    </span>
                  )}
                  {playlist.userId !== userId && (
                    <span className="badge badge-shared" title={t('playlists.partagee')}>
                      <Users size={14} />
                    </span>
                  )}
                </div>
              </div>

              <div className="playlist-langues">
                <span>{getLangueFlag(playlist.langueSource)}</span>
                {playlist.typeMot && <span className="type-badge">{playlist.typeMot}</span>}
              </div>

              {playlist.description && (
                <p className="playlist-description">{playlist.description}</p>
              )}

              {playlist.theme && (
                <span className="playlist-theme">{playlist.theme}</span>
              )}

              <div className="playlist-stats">
                <span>{t('playlists.motsCount', { count: playlist.motCount || 0 })}</span>
                {playlist.pendingCount > 0 && (
                  <span className="pending-badge">{t('playlists.enAttente', { count: playlist.pendingCount })}</span>
                )}
              </div>

              <div className="playlist-actions">
                <button
                  className="btn-action btn-view"
                  onClick={() => handleOpenDetails(playlist)}
                  title={t('playlists.voirLesMots')}
                >
                  <BookOpen size={16} />
                </button>
                {playlist.userId === userId && (
                  <>
                    <button
                      className="btn-action btn-edit"
                      onClick={() => handleEdit(playlist)}
                      title={t('playlists.modifier')}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-action btn-delete"
                      onClick={() => handleDelete(playlist)}
                      title={t('playlists.supprimer')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal création/édition */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingPlaylist ? t('playlists.modifierPlaylist') : t('playlists.nouvellePlaylistTitre')}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('playlists.nomLabel')}</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                  placeholder={t('playlists.nomPlaceholder')}
                  autoCapitalize="off"
                />
              </div>

              <div className="form-group">
                <label>{t('playlists.descriptionLabel')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('playlists.descriptionPlaceholder')}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>{t('playlists.langueLabel')}</label>
                <select
                  value={formData.langueSource}
                  onChange={(e) => setFormData({ ...formData, langueSource: e.target.value })}
                  disabled={editingPlaylist}
                >
                  {langues.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.flag} {l.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('playlists.typeMotLabel')}</label>
                <select
                  value={formData.typeMot}
                  onChange={(e) => setFormData({ ...formData, typeMot: e.target.value })}
                >
                  {typesMots.map((tm) => (
                    <option key={tm.value} value={tm.value} disabled={tm.value === ''}>
                      {tm.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('playlists.themeLabel')}</label>
                <select
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                >
                  {themes.map((th) => (
                    <option key={th.value} value={th.value}>
                      {th.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isPublic}
                    onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                  />
                  <Globe size={16} />
                  {t('playlists.playlistPublique')}
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowCreateModal(false)}>
                  {t('playlists.annuler')}
                </button>
                <button type="submit" className="btn-submit">
                  {editingPlaylist ? t('playlists.modifier') : t('playlists.creer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal détails playlist */}
      {selectedPlaylist && (
        <div className="modal-overlay" onClick={() => { setSelectedPlaylist(null); fetchPlaylists(); }}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedPlaylist.nom}</h2>
              <div className="modal-header-actions">
                <button
                  className="btn-import"
                  onClick={() => {
                    setShowImportModal(true);
                    setImportText('');
                    setImportResults(null);
                  }}
                >
                  <Upload size={16} />
                  {t('playlists.importer')}
                </button>
                <button
                  className="btn-close"
                  onClick={() => { setSelectedPlaylist(null); fetchPlaylists(); }}
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="playlist-info">
              <span>{getLangueFlag(selectedPlaylist.langueSource)}</span>
              {selectedPlaylist.theme && <span className="theme-badge">{selectedPlaylist.theme}</span>}
            </div>

            {selectedPlaylist.PlaylistMots && selectedPlaylist.PlaylistMots.length > 0 ? (
              <div className="mots-list">
                <p className="mots-count">{t('playlists.motsCount', { count: selectedPlaylist.PlaylistMots.length })}</p>
                {selectedPlaylist.PlaylistMots.map((pm) => (
                  <div key={pm.id} className="mot-item">
                    <div className="mot-content">
                      <strong>{pm.Mot.motSingulier}</strong>
                      {pm.Mot.genre && <span className="genre">({pm.Mot.genre})</span>}
                      {pm.Mot.traductionsSource && pm.Mot.traductionsSource.length > 0 && (
                        <span className="traduction">
                          → {pm.Mot.traductionsSource
                            .filter(tr => tr.motCible.langue === selectedPlaylist.langueCible)
                            .map(tr => tr.motCible.motSingulier)
                            .join(', ')}
                        </span>
                      )}
                    </div>
                    {selectedPlaylist.userId === userId && (
                      <button
                        className="btn-remove"
                        onClick={() => handleRemoveMot(pm.Mot.id)}
                        title={t('playlists.retirerDeLaPlaylist')}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-mots">
                <p>{t('playlists.aucunMotPlaylist')}</p>
                <p className="hint">{t('playlists.utilisezImporter')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal import */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{t('playlists.importerDesMots')}</h2>
            <p className="import-hint">
              {t('playlists.importHint')}
              <br />
              <small>{t('playlists.motsConnusAjoutes')}</small>
              <br />
              <small>{t('playlists.motsInconnusAttente')}</small>
            </p>

            <textarea
              className="import-textarea"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder={t('playlists.importPlaceholder')}
              rows={10}
              autoCapitalize="off"
            />

            {importResults && (
              <div className="import-results">
                <h4>{t('playlists.resultatsImport')}</h4>
                <ul>
                  <li className="success">{t('playlists.motsExistantsAjoutes', { count: importResults.summary.found })}</li>
                  <li className="pending">{t('playlists.motsEnAttenteValidation', { count: importResults.summary.pending })}</li>
                  <li className="warning">{t('playlists.motsDejaPresents', { count: importResults.summary.alreadyIn })}</li>
                  {importResults.summary.alreadyPending > 0 && (
                    <li className="info">{t('playlists.motsDejaEnAttente', { count: importResults.summary.alreadyPending })}</li>
                  )}
                </ul>
                {importResults.results.pending && importResults.results.pending.length > 0 && (
                  <div className="pending-list">
                    <p>{t('playlists.motsEnAttenteListe')}</p>
                    <code>{importResults.results.pending.map(m => m.motOriginal).join(', ')}</code>
                    <p className="hint">{t('playlists.adminValideraHint')}</p>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              {importResults ? (
                <button type="button" className="btn-submit" onClick={() => {
                  setShowImportModal(false);
                  handleOpenDetails(selectedPlaylist);
                  fetchPlaylists();
                }}>
                  {t('playlists.fermer')}
                </button>
              ) : (
                <>
                  <button type="button" className="btn-cancel" onClick={() => setShowImportModal(false)}>
                    {t('playlists.fermer')}
                  </button>
                  <button type="button" className="btn-submit" onClick={handleImport}>
                    <Upload size={16} />
                    {t('playlists.importer')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
